// Page Détail d'une Annonce - Entre Particuliers
// Mode Préouverture - Test

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Heart, Share2, MapPin, Clock, User, Phone, Mail,
  MessageCircle, Shield, AlertTriangle, ChevronLeft, ChevronRight,
  Package, Eye, Flag, CheckCircle, Info, Star, Calendar, Tag,
  Play, Video, BadgeCheck, ExternalLink
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import MainNavigation from '../../components/layout/MainNavigation';
import { API_URL } from '../../config/api';

// ══════════════════════════════════════════════════════════════════════════════
// DONNÉES DE TEST - PRÉOUVERTURE
// ══════════════════════════════════════════════════════════════════════════════

const MOCK_ANNONCES = [
  {
    id: 'test-1',
    title: 'Perceuse visseuse Makita 18V - Excellent état',
    description: 'Perceuse visseuse sans fil Makita DDF484 18V LXT. Utilisée quelques fois seulement, comme neuve. Livrée avec 2 batteries 5Ah, chargeur rapide et coffret Makpac. Parfaite pour bricoleurs exigeants.',
    price: 189,
    price_negotiable: true,
    condition: 'Très bon état',
    category: 'electricite',
    subcategory: 'outillage_elec',
    brand: 'Makita',
    city: 'Lyon',
    postal_code: '69003',
    photos: [
      'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=800',
      'https://images.unsplash.com/photo-1572981779307-38b8cabb2407?w=800'
    ],
    seller: { name: 'Jean-Pierre D.', member_since: '2024', verified: true, rating: 4.8, sales: 12 },
    views: 234,
    favorites: 18,
    created_at: new Date(Date.now() - 2 * 3600000).toISOString()
  },
  {
    id: 'test-2',
    title: 'Lot carrelage imitation bois 45m² - Neuf',
    description: 'Carrelage grès cérame imitation parquet chêne naturel. Format 20x120cm, épaisseur 9mm. Reste de chantier, jamais posé, encore emballé. Antidérapant R10, idéal pièces humides.',
    price: 650,
    price_negotiable: true,
    condition: 'Neuf',
    category: 'carrelage',
    subcategory: 'carreaux',
    brand: 'Porcelanosa',
    city: 'Marseille',
    postal_code: '13008',
    photos: [
      'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=800'
    ],
    seller: { name: 'Sophie M.', member_since: '2023', verified: true, rating: 5.0, sales: 8 },
    views: 456,
    favorites: 34,
    created_at: new Date(Date.now() - 5 * 3600000).toISOString()
  },
  {
    id: 'test-3',
    title: 'Échafaudage aluminium complet - Hauteur 8m',
    description: "Échafaudage professionnel en aluminium, hauteur max 8m. Plateaux, garde-corps, escaliers, roulettes avec freins. Conforme aux normes. Démonté, prêt à emporter. Facture d'origine disponible.",
    price: 1200,
    price_negotiable: false,
    condition: 'Bon état',
    category: 'maconnerie',
    subcategory: 'echafaudages',
    brand: 'Tubesca',
    city: 'Toulouse',
    postal_code: '31000',
    photos: [
      'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800'
    ],
    seller: { name: 'Marc L.', member_since: '2022', verified: true, rating: 4.9, sales: 23 },
    views: 189,
    favorites: 12,
    created_at: new Date(Date.now() - 24 * 3600000).toISOString()
  },
  {
    id: 'test-4',
    title: 'Robot tondeuse Husqvarna 315X - Comme neuf',
    description: 'Robot tondeuse Husqvarna Automower 315X. Surface jusqu\'à 1600m². GPS, connexion smartphone, coupe silencieuse. Utilisé 1 saison. Câble périphérique et station de charge inclus.',
    price: 1450,
    price_negotiable: true,
    condition: 'Très bon état',
    category: 'jardin',
    subcategory: 'tondeuses',
    brand: 'Husqvarna',
    city: 'Bordeaux',
    postal_code: '33000',
    photos: [
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800'
    ],
    seller: { name: 'Pierre R.', member_since: '2023', verified: true, rating: 4.7, sales: 5 },
    views: 567,
    favorites: 45,
    created_at: new Date(Date.now() - 12 * 3600000).toISOString()
  },
  {
    id: 'test-5',
    title: 'Tableau électrique Schneider pré-câblé 4 rangées',
    description: 'Tableau électrique Schneider Resi9 4 rangées, 52 modules. Pré-équipé disjoncteurs, différentiels 30mA. Conforme NF C 15-100. Parfait pour rénovation complète.',
    price: 320,
    price_negotiable: false,
    condition: 'Neuf',
    category: 'electricite',
    subcategory: 'tableaux',
    brand: 'Schneider',
    city: 'Nantes',
    postal_code: '44000',
    photos: [
      'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=800'
    ],
    seller: { name: 'François B.', member_since: '2024', verified: true, rating: 5.0, sales: 3 },
    views: 145,
    favorites: 8,
    created_at: new Date(Date.now() - 48 * 3600000).toISOString()
  },
  {
    id: 'test-6',
    title: 'Pompe à chaleur Daikin Altherma 3 - 8kW',
    description: 'PAC air/eau Daikin Altherma 3, puissance 8kW. Chauffage + ECS. Compresseur R32. Déposée lors rénovation, fonctionne parfaitement. Documentation et facture origine.',
    price: 2800,
    price_negotiable: true,
    condition: 'Bon état',
    category: 'chauffage',
    subcategory: 'pac',
    brand: 'Daikin',
    city: 'Lille',
    postal_code: '59000',
    photos: [
      'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=800'
    ],
    seller: { name: 'Antoine V.', member_since: '2022', verified: true, rating: 4.6, sales: 18 },
    views: 678,
    favorites: 52,
    created_at: new Date(Date.now() - 72 * 3600000).toISOString()
  },
  {
    id: 'test-7',
    title: 'Lot peinture Tollens blanc mat 80L',
    description: 'Peinture murale Tollens Idrotop blanc mat. 8 pots de 10L neufs jamais ouverts. Reste de chantier professionnel. Monocouche, lessivable, rendement 12m²/L.',
    price: 280,
    price_negotiable: true,
    condition: 'Neuf',
    category: 'peinture',
    subcategory: 'peintures_int',
    brand: 'Tollens',
    city: 'Strasbourg',
    postal_code: '67000',
    photos: [
      'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800'
    ],
    seller: { name: 'David K.', member_since: '2023', verified: true, rating: 4.9, sales: 15 },
    views: 234,
    favorites: 19,
    created_at: new Date(Date.now() - 6 * 3600000).toISOString()
  },
  {
    id: 'test-8',
    title: 'Scie circulaire Festool TS 55 REQ + Rail',
    description: 'Scie plongeante Festool TS 55 REBQ-Plus avec rail de guidage 1400mm. Systainer, lame neuve 48 dents. Aspiration intégrée. La référence pour coupes parfaites.',
    price: 520,
    price_negotiable: false,
    condition: 'Très bon état',
    category: 'menuiserie',
    subcategory: 'machines_bois',
    brand: 'Festool',
    city: 'Nice',
    postal_code: '06000',
    photos: [
      'https://images.unsplash.com/photo-1616401784845-180882ba9ba8?w=800'
    ],
    seller: { name: 'Philippe G.', member_since: '2021', verified: true, rating: 4.8, sales: 31 },
    views: 389,
    favorites: 28,
    created_at: new Date(Date.now() - 18 * 3600000).toISOString()
  },
  {
    id: 'test-9',
    title: 'Kit panneaux solaires 3kWc complet',
    description: 'Kit photovoltaïque complet : 8 panneaux 375Wc monocristallins + onduleur Enphase + fixations toiture tuiles. Installation facile. Garantie 25 ans panneaux.',
    price: 3200,
    price_negotiable: true,
    condition: 'Neuf',
    category: 'solaire',
    subcategory: 'kits_solaires',
    brand: 'Longi',
    city: 'Montpellier',
    postal_code: '34000',
    photos: [
      'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800'
    ],
    seller: { name: 'Julien H.', member_since: '2023', verified: true, rating: 5.0, sales: 7 },
    views: 892,
    favorites: 67,
    created_at: new Date(Date.now() - 4 * 3600000).toISOString()
  },
  {
    id: 'test-10',
    title: 'Bétonnière électrique 160L Altrad',
    description: 'Bétonnière électrique Altrad BT160, cuve 160L. Moteur 650W. Châssis renforcé, roues. Parfait pour travaux de maçonnerie. Utilisée sur 2 chantiers.',
    price: 280,
    price_negotiable: true,
    condition: 'Bon état',
    category: 'maconnerie',
    subcategory: 'outillage_macon',
    brand: 'Altrad',
    city: 'Rennes',
    postal_code: '35000',
    photos: [
      'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800'
    ],
    seller: { name: 'Yann L.', member_since: '2022', verified: true, rating: 4.5, sales: 9 },
    views: 156,
    favorites: 11,
    created_at: new Date(Date.now() - 36 * 3600000).toISOString()
  },
  {
    id: 'test-11',
    title: 'Radiateur électrique Atlantic 2000W x4',
    description: 'Lot 4 radiateurs Atlantic Nirvana Neo 2000W. Connectés, programmables, détection fenêtre ouverte. Déposés suite changement chauffage. Comme neufs.',
    price: 800,
    price_negotiable: true,
    condition: 'Très bon état',
    category: 'chauffage',
    subcategory: 'radiateurs',
    brand: 'Atlantic',
    city: 'Grenoble',
    postal_code: '38000',
    photos: [
      'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=800'
    ],
    seller: { name: 'Christophe M.', member_since: '2023', verified: true, rating: 4.7, sales: 14 },
    views: 267,
    favorites: 21,
    created_at: new Date(Date.now() - 8 * 3600000).toISOString()
  },
  {
    id: 'test-12',
    title: 'Perforateur Bosch GBH 18V-26 + Batteries',
    description: 'Perforateur sans fil Bosch Professional GBH 18V-26 F. Mode perçage, perfo, burinage. 2 batteries ProCore 8Ah, chargeur GAL 18V-160 C. Coffret L-BOXX.',
    price: 450,
    price_negotiable: false,
    condition: 'Bon état',
    category: 'electricite',
    subcategory: 'outillage_elec',
    brand: 'Bosch',
    city: 'Dijon',
    postal_code: '21000',
    photos: [
      'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=800'
    ],
    seller: { name: 'Nicolas P.', member_since: '2024', verified: true, rating: 4.9, sales: 6 },
    views: 345,
    favorites: 25,
    created_at: new Date(Date.now() - 15 * 3600000).toISOString()
  },
  {
    id: 'test-13',
    title: 'Robot piscine Dolphin E35 - Garantie',
    description: 'Robot nettoyeur piscine Dolphin E35. Fond, parois, ligne d\'eau. Cycle 2h30. Télécommande incluse. Utilisé 2 saisons, révisé. Garantie transférable 1 an.',
    price: 680,
    price_negotiable: true,
    condition: 'Bon état',
    category: 'piscine',
    subcategory: 'robots_piscine',
    brand: 'Dolphin',
    city: 'Aix-en-Provence',
    postal_code: '13100',
    photos: [
      'https://images.unsplash.com/photo-1575429198097-0414ec08e8cd?w=800'
    ],
    seller: { name: 'Olivier S.', member_since: '2022', verified: true, rating: 4.8, sales: 11 },
    views: 423,
    favorites: 33,
    created_at: new Date(Date.now() - 20 * 3600000).toISOString()
  },
  {
    id: 'test-14',
    title: 'Velux GGL 114x118 + Store occultant',
    description: 'Fenêtre de toit Velux GGL MK06 bois, 114x118cm. Double vitrage Confort. Volet roulant solaire SSL + store occultant DKL. Jamais posés, emballage origine.',
    price: 890,
    price_negotiable: false,
    condition: 'Neuf',
    category: 'renovation',
    subcategory: 'portes',
    brand: 'Velux',
    city: 'Le Havre',
    postal_code: '76600',
    photos: [
      'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800'
    ],
    seller: { name: 'Laurent D.', member_since: '2023', verified: true, rating: 5.0, sales: 4 },
    views: 234,
    favorites: 17,
    created_at: new Date(Date.now() - 10 * 3600000).toISOString()
  },
  {
    id: 'test-15',
    title: 'Meuleuse Hilti AG 125-A36 + Batterie',
    description: 'Meuleuse d\'angle sans fil Hilti 36V, disque 125mm. Batterie B36/5.2 Li-Ion + chargeur C4/36-350. Protection anti-kickback. Très peu servie.',
    price: 380,
    price_negotiable: true,
    condition: 'Très bon état',
    category: 'electricite',
    subcategory: 'outillage_elec',
    brand: 'Hilti',
    city: 'Toulon',
    postal_code: '83000',
    photos: [
      'https://images.unsplash.com/photo-1572981779307-38b8cabb2407?w=800'
    ],
    seller: { name: 'Thierry B.', member_since: '2021', verified: true, rating: 4.6, sales: 27 },
    views: 189,
    favorites: 14,
    created_at: new Date(Date.now() - 28 * 3600000).toISOString()
  },
  {
    id: 'test-16',
    title: 'Isolation laine de roche 200mm - 35m²',
    description: 'Panneaux laine de roche Rockwool 200mm. R=5.7. Reste chantier isolation combles. 7 paquets neufs sous film. Classement feu A1.',
    price: 420,
    price_negotiable: true,
    condition: 'Neuf',
    category: 'renovation',
    subcategory: 'isolation',
    brand: 'Rockwool',
    city: 'Clermont-Ferrand',
    postal_code: '63000',
    photos: [
      'https://images.unsplash.com/photo-1607400201889-565b1ee75f8e?w=800'
    ],
    seller: { name: 'Bruno C.', member_since: '2023', verified: true, rating: 4.9, sales: 8 },
    views: 134,
    favorites: 9,
    created_at: new Date(Date.now() - 44 * 3600000).toISOString()
  },
  {
    id: 'test-17',
    title: 'Robinetterie Grohe Essence - Lot cuisine + SDB',
    description: 'Mitigeur évier Grohe Essence + Mitigeur lavabo + Colonne douche Rainshower. Finition chromée. Neufs dans emballages. Prix boutique > 1200€.',
    price: 580,
    price_negotiable: true,
    condition: 'Neuf',
    category: 'plomberie',
    subcategory: 'robinetterie',
    brand: 'Grohe',
    city: 'Angers',
    postal_code: '49000',
    photos: [
      'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800'
    ],
    seller: { name: 'Stéphane R.', member_since: '2024', verified: true, rating: 5.0, sales: 2 },
    views: 312,
    favorites: 24,
    created_at: new Date(Date.now() - 7 * 3600000).toISOString()
  },
  {
    id: 'test-18',
    title: 'Coupe-carreaux électrique Rubi DU-200 EVO',
    description: 'Coupe-carreaux sur table Rubi DU-200 EVO, coupe jusqu\'à 52cm. Laser intégré, bac eau. Table inclinable 45°. État impeccable, peu utilisé.',
    price: 650,
    price_negotiable: false,
    condition: 'Très bon état',
    category: 'carrelage',
    subcategory: 'decoupe',
    brand: 'Rubi',
    city: 'Metz',
    postal_code: '57000',
    photos: [
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800'
    ],
    seller: { name: 'Eric F.', member_since: '2022', verified: true, rating: 4.7, sales: 19 },
    views: 178,
    favorites: 13,
    created_at: new Date(Date.now() - 32 * 3600000).toISOString()
  },
  {
    id: 'test-19',
    title: 'Débroussailleuse Stihl FS 131 - Pro',
    description: 'Débroussailleuse thermique Stihl FS 131, moteur 4-MIX 36.3cc. Harnais ErgoStart, guidon vélo. Tête fil + lame 3 dents. Entretien concessionnaire.',
    price: 420,
    price_negotiable: true,
    condition: 'Bon état',
    category: 'jardin',
    subcategory: 'taille',
    brand: 'Stihl',
    city: 'Perpignan',
    postal_code: '66000',
    photos: [
      'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800'
    ],
    seller: { name: 'Michel A.', member_since: '2021', verified: true, rating: 4.8, sales: 22 },
    views: 267,
    favorites: 18,
    created_at: new Date(Date.now() - 16 * 3600000).toISOString()
  },
  {
    id: 'test-20',
    title: 'Chauffe-eau thermodynamique Atlantic 250L',
    description: 'Chauffe-eau thermodynamique Atlantic Calypso 250L. COP 3.2. Installé 2 ans, démonté suite déménagement. Parfait état de marche. Mode boost.',
    price: 950,
    price_negotiable: true,
    condition: 'Bon état',
    category: 'plomberie',
    subcategory: 'chauffe_eau',
    brand: 'Atlantic',
    city: 'Caen',
    postal_code: '14000',
    photos: [
      'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=800'
    ],
    seller: { name: 'Sébastien N.', member_since: '2023', verified: true, rating: 4.9, sales: 10 },
    views: 445,
    favorites: 31,
    created_at: new Date(Date.now() - 22 * 3600000).toISOString()
  }
];

export { MOCK_ANNONCES };

// ══════════════════════════════════════════════════════════════════════════════
// VIDÉOS & POSTS DU RÉSEAU SOCIAL LIÉS AUX ANNONCES
// ══════════════════════════════════════════════════════════════════════════════

const DEMO_LINKED_MEDIA_ANNONCES = {
  'test-1': [
    {
      id: 'media_ann_1',
      type: 'reel',
      thumbnail: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=400&h=500&fit=crop',
      author: { name: 'Jean-Pierre D.', color: '#3B82F6', verified: true },
      views: 3400,
      likes: 78,
      duration: 35,
      caption: 'Ma perceuse Makita à vendre - Démonstration !',
      timeAgo: 'Il y a 3j'
    }
  ],
  'test-2': [
    {
      id: 'media_ann_2',
      type: 'photo',
      thumbnail: 'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=400&h=500&fit=crop',
      author: { name: 'Sophie M.', color: '#EC4899', verified: true },
      likes: 123,
      caption: 'Carrelage imitation bois - Voir le rendu !',
      timeAgo: 'Il y a 5j'
    }
  ],
  'default': [
    {
      id: 'media_ann_default',
      type: 'reel',
      thumbnail: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=500&fit=crop',
      author: { name: 'Vendeur', color: '#10B981', verified: false },
      views: 1200,
      likes: 45,
      duration: 20,
      caption: 'Présentation de mon annonce',
      timeAgo: 'Il y a 2j'
    }
  ]
};

// Composant Section Médias Liés pour Annonces
function LinkedMediaSectionAnnonce({ annonceId, annonceName }) {
  const navigate = useNavigate();
  const linkedMedia = DEMO_LINKED_MEDIA_ANNONCES[annonceId] || DEMO_LINKED_MEDIA_ANNONCES['default'];
  
  if (linkedMedia.length === 0) return null;
  
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm mt-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Video size={20} className="text-[#F97316]" />
          <h2 className="text-lg font-bold text-gray-900">Vidéos & Posts associés</h2>
        </div>
        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
          DÉMO
        </span>
      </div>
      
      <p className="text-sm text-gray-500 mb-4">
        Le vendeur a partagé du contenu sur cette annonce
      </p>
      
      {/* Grille de médias */}
      <div className="grid grid-cols-2 gap-3">
        {linkedMedia.map((media) => (
          <motion.div
            key={media.id}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate(`/reseau/reels?post=${media.id}`)}
            className="relative aspect-[4/5] rounded-xl overflow-hidden cursor-pointer group"
          >
            <img 
              src={media.thumbnail} 
              alt="" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
            
            {media.type === 'reel' && (
              <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 backdrop-blur-sm text-white text-[10px] font-medium px-2 py-1 rounded-full">
                <Play size={10} fill="white" />
                {Math.floor(media.duration / 60)}:{(media.duration % 60).toString().padStart(2, '0')}
              </div>
            )}
            
            {media.type === 'reel' && media.views && (
              <div className="absolute bottom-12 left-2 flex items-center gap-1 text-white text-xs">
                <Eye size={12} />
                {media.views.toLocaleString()}
              </div>
            )}
            
            <div className="absolute bottom-2 left-2 right-2">
              <div className="flex items-center gap-2">
                <div 
                  className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                  style={{ backgroundColor: media.author.color }}
                >
                  {media.author.name[0]}
                </div>
                <span className="text-white text-xs font-medium truncate">
                  {media.author.name.split(' ')[0]}
                </span>
                {media.author.verified && (
                  <BadgeCheck size={10} className="text-blue-400 fill-blue-400" />
                )}
              </div>
            </div>
            
            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                <Play size={24} className="text-white" fill="white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      
      <Link 
        to="/reseau"
        className="mt-4 flex items-center justify-center gap-2 text-sm text-[#F97316] font-medium hover:underline"
      >
        Voir plus sur le Réseau
        <ExternalLink size={14} />
      </Link>
    </div>
  );
}

export default function AnnonceDetailPage() {
  const { annonceId } = useParams();
  const navigate = useNavigate();
  const [annonce, setAnnonce] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    loadAnnonce();
    checkFavorite();
  }, [annonceId]);

  const loadAnnonce = async () => {
    setLoading(true);
    try {
      // D'abord chercher dans les données de test
      const mockAnnonce = MOCK_ANNONCES.find(a => a.id === annonceId);
      if (mockAnnonce) {
        setAnnonce(mockAnnonce);
        setLoading(false);
        return;
      }

      // Sinon chercher via API (pour de futures vraies annonces)
      const response = await fetch(`${API_URL}/api/ecosystem/listings/${annonceId}`);
      if (response.ok) {
        const data = await response.json();
        setAnnonce(data);
      }
    } catch (error) {
      console.error('Erreur chargement annonce:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkFavorite = () => {
    const favorites = JSON.parse(localStorage.getItem('annonces_favoris') || '[]');
    setIsFavorite(favorites.includes(annonceId));
  };

  const toggleFavorite = () => {
    const favorites = JSON.parse(localStorage.getItem('annonces_favoris') || '[]');
    if (isFavorite) {
      const newFavorites = favorites.filter(id => id !== annonceId);
      localStorage.setItem('annonces_favoris', JSON.stringify(newFavorites));
      toast.success('Retiré des favoris');
    } else {
      favorites.push(annonceId);
      localStorage.setItem('annonces_favoris', JSON.stringify(favorites));
      toast.success('Ajouté aux favoris');
    }
    setIsFavorite(!isFavorite);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: annonce?.title,
          text: `Découvrez cette annonce sur AbracadaBati : ${annonce?.title}`,
          url: window.location.href
        });
      } catch (err) {
        console.log('Partage annulé');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Lien copié dans le presse-papier');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <UnifiedHeader />
        <MainNavigation />
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!annonce) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <UnifiedHeader />
        <MainNavigation />
        <div className="h-24 md:hidden" />
        <main className="max-w-3xl mx-auto px-4 py-12 text-center">
          <Package size={64} className="mx-auto text-gray-300 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Annonce introuvable</h1>
          <p className="text-gray-500 mb-6">Cette annonce n'existe pas ou a été supprimée.</p>
          <Link to="/particuliers">
            <Button className="bg-[#1E40AF] hover:bg-[#1E3A8A]">
              Retour aux annonces
            </Button>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <UnifiedHeader />
      <MainNavigation />
      <div className="h-24 md:hidden" />

      {/* Header avec retour */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft size={20} />
              <span className="text-sm font-medium">Retour</span>
            </button>
            
            <div className="flex items-center gap-2">
              <button
                onClick={toggleFavorite}
                className={`p-2 rounded-lg transition-colors ${
                  isFavorite ? 'bg-red-50 text-red-500' : 'bg-gray-100 text-gray-600 hover:text-red-500'
                }`}
              >
                <Heart size={20} fill={isFavorite ? 'currentColor' : 'none'} />
              </button>
              <button
                onClick={handleShare}
                className="p-2 bg-gray-100 text-gray-600 hover:text-gray-900 rounded-lg transition-colors"
              >
                <Share2 size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-5 gap-6">
          
          {/* Colonne gauche - Images et Description */}
          <div className="lg:col-span-3 space-y-4">
            
            {/* Galerie photos */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
              <div className="relative aspect-[4/3] bg-gray-100">
                {annonce.photos && annonce.photos.length > 0 ? (
                  <>
                    <img
                      src={annonce.photos[currentImageIndex]}
                      alt={annonce.title}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Badge TEST */}
                    <div className="absolute top-4 left-4 bg-amber-500 text-white px-3 py-1.5 rounded-lg text-sm font-bold flex items-center gap-1.5 shadow-lg">
                      <Info size={16} />
                      ANNONCE TEST
                    </div>
                    
                    {annonce.photos.length > 1 && (
                      <>
                        <button
                          onClick={() => setCurrentImageIndex(i => i > 0 ? i - 1 : annonce.photos.length - 1)}
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                        >
                          <ChevronLeft size={24} className="text-gray-700" />
                        </button>
                        <button
                          onClick={() => setCurrentImageIndex(i => i < annonce.photos.length - 1 ? i + 1 : 0)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                        >
                          <ChevronRight size={24} className="text-gray-700" />
                        </button>
                        
                        {/* Indicateurs */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                          {annonce.photos.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={() => setCurrentImageIndex(idx)}
                              className={`w-2.5 h-2.5 rounded-full transition-all ${
                                idx === currentImageIndex ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/70'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package size={64} className="text-gray-300" />
                  </div>
                )}
              </div>
              
              {/* Thumbnails */}
              {annonce.photos && annonce.photos.length > 1 && (
                <div className="p-3 flex gap-2 overflow-x-auto">
                  {annonce.photos.map((photo, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-colors ${
                        idx === currentImageIndex ? 'border-[#1E40AF]' : 'border-transparent'
                      }`}
                    >
                      <img src={photo} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Description */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Description</h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {annonce.description}
              </p>
              
              {/* Caractéristiques */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <h3 className="text-sm font-bold text-gray-900 mb-3">Caractéristiques</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Tag size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-600">État : <span className="font-medium text-gray-900">{annonce.condition}</span></span>
                  </div>
                  {annonce.brand && (
                    <div className="flex items-center gap-2">
                      <Star size={16} className="text-gray-400" />
                      <span className="text-sm text-gray-600">Marque : <span className="font-medium text-gray-900">{annonce.brand}</span></span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-600">Publié : <span className="font-medium text-gray-900">{formatTimeAgo(annonce.created_at)}</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Eye size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-600">Vues : <span className="font-medium text-gray-900">{annonce.views}</span></span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* ══════════════════════════════════════════════════════════════════
                SECTION VIDÉOS & POSTS DU RÉSEAU SOCIAL
                ══════════════════════════════════════════════════════════════════ */}
            <LinkedMediaSectionAnnonce 
              annonceId={annonce.id} 
              annonceName={annonce.title} 
            />
          </div>

          {/* Colonne droite - Prix et Vendeur */}
          <div className="lg:col-span-2 space-y-4">
            
            {/* Carte Prix */}
            <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-20">
              <h1 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                {annonce.title}
              </h1>
              
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-3xl font-bold text-[#F97316]">
                  {formatPrice(annonce.price)}
                </span>
                {annonce.price_negotiable && (
                  <span className="text-sm text-gray-500 font-medium">Négociable</span>
                )}
              </div>

              <div className="flex items-center gap-2 text-gray-600 mb-6">
                <MapPin size={16} />
                <span className="text-sm">{annonce.city} ({annonce.postal_code})</span>
              </div>

              {/* Alerte mode test */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Mode Préouverture</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Cette annonce est un exemple. La plateforme sera bientôt opérationnelle pour les transactions !
                    </p>
                  </div>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="space-y-3">
                <Button 
                  className="w-full h-12 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold rounded-xl"
                  onClick={() => toast.info('Fonctionnalité bientôt disponible !')}
                >
                  <MessageCircle size={18} className="mr-2" />
                  Contacter le vendeur
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full h-12 border-2 border-[#1E40AF] text-[#1E40AF] hover:bg-[#1E40AF]/5 font-semibold rounded-xl"
                  onClick={() => toast.info('Fonctionnalité bientôt disponible !')}
                >
                  <Phone size={18} className="mr-2" />
                  Voir le téléphone
                </Button>
              </div>
            </div>

            {/* Carte Vendeur */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-sm font-bold text-gray-900 mb-4">Vendeur</h3>
              
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#1E40AF] to-[#3B82F6] flex items-center justify-center text-white font-bold text-lg">
                  {annonce.seller?.name?.charAt(0) || 'V'}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">{annonce.seller?.name}</span>
                    {annonce.seller?.verified && (
                      <CheckCircle size={16} className="text-emerald-500" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500">Membre depuis {annonce.seller?.member_since}</p>
                </div>
              </div>

              {/* Stats vendeur */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-amber-500 mb-1">
                    <Star size={14} fill="currentColor" />
                    <span className="font-bold">{annonce.seller?.rating}</span>
                  </div>
                  <p className="text-xs text-gray-500">Note moyenne</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="font-bold text-gray-900">{annonce.seller?.sales}</p>
                  <p className="text-xs text-gray-500">Ventes réalisées</p>
                </div>
              </div>

              {/* Badge sécurité */}
              <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg">
                <Shield size={18} className="text-emerald-600" />
                <span className="text-xs text-emerald-700 font-medium">Identité vérifiée par AbracadaBati</span>
              </div>
            </div>

            {/* Signaler */}
            <button className="w-full flex items-center justify-center gap-2 py-3 text-gray-500 hover:text-red-500 transition-colors text-sm">
              <Flag size={16} />
              Signaler cette annonce
            </button>
          </div>
        </div>
      </main>

      {/* Spacer pour mobile */}
      <div className="h-24 lg:hidden" />
    </div>
  );
}
